import { openWithTenant } from '@app-store'
import {
  Checkbox,
  ControlledDropDown,
  ControlledMultiSelect,
  CustomRadioGroup,
  DATE_FIELD_TYPES,
  DropdownType,
  FormDateInput,
  FormInput,
  MultiLineInput,
  openNewWindow,
  PhoneInput,
} from '@ui-kit'
import { debounce, isEmpty } from 'lodash'
import React, { useEffect, useMemo, useState } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { HybridLayoutDefaultFieldPropsType } from '../hybridLayout.types'
import { formatLocalDate } from '../../../utils/date.utils'
import {
  componentNames,
  DATE_RESTRICTION_TYPES,
  DECIMAL_REGEX,
  EMAIL_REGEX,
  NAMESPACES,
  NUMERIC_REGEX,
  PHONE_REGEX,
  premitiveDataTypes,
} from '../../../utils/constants'
import { entitiesRoute, viewRoute } from '../../../routes/route.constant'
import { LayoutResponseType } from '@app-store'
import { ElementType } from '../../../types/layout.types'
import {
  getValidationsFromElement,
  getSiblingElementKeyName,
} from '../../../utils/form.utils'
import { useFormBehaviorFieldOverrides } from '../../../form-behavior/useFormBehaviorFieldOverrides'

export const HybridFieldViewRenderer = ({
  element,
  fullLayout,
}: {
  element: ElementType
  fullLayout?: LayoutResponseType
}) => {
  const field = useWatch({ name: element?.keyName })
  const singleLookupId = useWatch({
    name: element?.parent ? `${element?.parent?.lookupKeyName}.id` : `__NULL__`,
  })
  const recordId = useWatch({ name: getSiblingElementKeyName(element, 'id') })
  const [isHyperLink, setIsHyperlink] = useState<boolean>(false)

  const checkHyperLink = () => {
    if (!field) return false

    const hyperlinkTypes = [
      componentNames.URL,
      componentNames.WEBSITE,
      componentNames.EMAIL,
      componentNames.UNIQUE_ID,
    ] as string[]

    if (element?.isCallable) {
      return true
    }

    // if (isThisAParentCaseIdHyperLink(element) && singleLookupValue?.id) {
    //   return true
    // }

    if (
      element?.elementName === componentNames.UNIQUE_ID &&
      element?.meta?.d_entity_id === fullLayout?.entity_id &&
      !window?.location?.pathname?.includes('/workflow/review/') &&
      !window?.location?.pathname?.includes('interaction') &&
      (fullLayout?.default_value || element?.meta?.d_entity_id === 32)
    ) {
      return false
    }

    return hyperlinkTypes.includes(element.elementName)
  }

  const onHyperlinkClick = () => {
    if (element?.elementName === componentNames.EMAIL) {
      openWithTenant(`mailto:${field}`, '_blank')
    }
    if (element?.elementName === componentNames.SINGLE_LOOKUP) {
      openNewWindow(
        `/entity/${element?.meta?.d_entity_id}/workflow/review/${field?.id}`
      )
    } else if (element?.elementName === componentNames.UNIQUE_ID) {
      const entityId = element?.meta?.d_entity_id

      if (!isEmpty(element?.parent)) {
        if (!singleLookupId) {
          return
        }
        openNewWindow(
          `/${entitiesRoute}/${entityId}/${viewRoute}/${singleLookupId}`
        )
      } else {
        if (!recordId || String(recordId) === 'undefined') {
          return
        }
        openNewWindow(`/${entitiesRoute}/${entityId}/${viewRoute}/${recordId}`)
      }
    } else {
      openWithTenant(field, '_blank')
    }
  }

  useEffect(() => {
    setIsHyperlink(checkHyperLink())
  }, [element, field])

  return (
    <div className="font-primary font-light">
      <label
        htmlFor={element?.keyName}
        className="text-sm font-normal mb-1 block"
      >
        {element?.meta?.label}
      </label>
      <div
        onClick={isHyperLink ? onHyperlinkClick : undefined}
        className={`text-xs font-extraLight ${
          isHyperLink ? 'text-secondary-500' : ''
        }`}
      >
        {(element?.elementName === componentNames.DATE_TIME
          ? formatLocalDate(field, 'MM/DD/YYYY', false)
          : field) || '-'}
      </div>
    </div>
  )
}

// DATE TIME
export const HybridLayoutDateTimeField: React.FC<HybridLayoutDefaultFieldPropsType> =
  React.memo(({ element, isDisabled, mode, fullLayout }) => {
    const { control } = useFormContext()

    const baseDisabled = !isEmpty(element?.parent)
      ? true
      : typeof isDisabled !== 'undefined'
      ? isDisabled
      : !!element?.isDisabled
    const {
      effectiveDisabled,
      effectiveRequired,
      effectiveHidden,
      effectiveMinDate,
      effectiveMaxDate,
      triggerFieldChange,
    } = useFormBehaviorFieldOverrides(
      fullLayout,
      element,
      baseDisabled,
      element?.meta?.required
    )

    if (effectiveHidden) {
      return null
    }

    const setupDateRange = () => {
      const today = new Date().toISOString().split('T')[0]
      const dateRange: { minDate: string; maxDate: string } = {
        minDate: '',
        maxDate: '',
      }
      if (
        element?.meta?.control_values?.[0] === DATE_RESTRICTION_TYPES?.FUTURE
      ) {
        dateRange.maxDate = today
      } else if (
        element?.meta?.control_values?.[0] === DATE_RESTRICTION_TYPES?.PAST
      ) {
        dateRange.minDate = today
      }
      return dateRange
    }
    const dateRange = setupDateRange()
    const formatDateTimeLocal = (value?: string) => {
      if (!value) return ''
      const date = new Date(value)
      return date.toISOString().slice(0, 16)
    }
    return (
      <div>
        {mode === 'view' ? (
          <HybridFieldViewRenderer element={element} fullLayout={fullLayout} />
        ) : (
          <Controller
            name={element?.keyName || ''}
            control={control}
            render={({ field }) => (
              <FormDateInput
                min={effectiveMinDate || dateRange.minDate}
                max={effectiveMaxDate || dateRange.maxDate}
                {...field}
                value={formatDateTimeLocal(field.value)}
                control={control}
                label={element?.meta?.label}
                isDisabled={effectiveDisabled}
                required={effectiveRequired}
                type={DATE_FIELD_TYPES.DATE_TIME_LOCAL}
                handleChange={(date?: string | null) => {
                  field.onChange(date)
                  triggerFieldChange()
                }}
              />
            )}
          />
        )}
      </div>
    )
  })
HybridLayoutDateTimeField.displayName = 'HybridLayoutDateTimeField'

// CHECKBOX
export const HybridLayoutCheckboxField: React.FC<HybridLayoutDefaultFieldPropsType> =
  React.memo(({ element, isDisabled }) => {
    const {
      register,
      formState: { errors },
    } = useFormContext()
    return (
      <div>
        <Checkbox
          labelClassName="break-all"
          label={element?.meta?.label || ''}
          name={element?.keyName || ''}
          register={register}
          disabled={
            !isEmpty(element?.parent)
              ? true
              : typeof isDisabled !== 'undefined'
              ? isDisabled
              : !!element?.isDisabled
          }
          errors={errors}
          required={element?.meta?.required ?? false}
        />
      </div>
    )
  })
HybridLayoutCheckboxField.displayName = 'HybridLayoutCheckboxField'

// RADIO
export const HybridLayoutRadioField: React.FC<HybridLayoutDefaultFieldPropsType> =
  React.memo(({ element, isDisabled, fullLayout, mode }) => {
    const {
      register,
      formState: { errors },
    } = useFormContext()
    const baseDisabled = !isEmpty(element?.parent)
      ? true
      : typeof isDisabled !== 'undefined'
      ? isDisabled
      : !!element?.isDisabled
    const {
      effectiveDisabled,
      effectiveRequired,
      effectiveHidden,
      triggerFieldChange,
      ruleKey,
    } = useFormBehaviorFieldOverrides(
      fullLayout,
      element,
      baseDisabled,
      element?.meta?.required ?? false
    )

    const getOptions = () => {
      const optionsArray = (element?.meta?.control_values || []).map(
        (opt: string) => ({
          label: opt,
          value: opt,
        })
      )
      return optionsArray
    }

    if (effectiveHidden) {
      return null
    }

    const registerWithFormBehavior: typeof register = (name, options) => {
      const registration = register(name, options)
      if (name !== (element.keyName || ruleKey)) {
        return registration
      }
      return {
        ...registration,
        onChange: (event) => {
          const result = registration.onChange(event)
          triggerFieldChange()
          return result
        },
      }
    }

    return (
      <div>
        {mode === 'view' ? (
          <HybridFieldViewRenderer element={element} fullLayout={fullLayout} />
        ) : (
          <>
            <div className="font-primary font-light">
              <label htmlFor={element?.keyName} className="text-xl font-medium">
                {element?.meta?.label}
                {effectiveRequired ? (
                  <span
                    className={`ml-1 text-xs leading-1 font-extraLight  ${
                      effectiveDisabled ? 'text-accent-300' : 'text-error-500'
                    }`}
                  >
                    *
                  </span>
                ) : null}
              </label>
            </div>
            <CustomRadioGroup
              tabIndex={element.tabIndex}
              options={getOptions()}
              register={registerWithFormBehavior}
              flexDirection={'flex flex-wrap'}
              name={element.keyName}
              disabled={effectiveDisabled}
              optionClasses={'text-sm font-normal'}
              labelClasses="w-full block"
              dataQaId={element.keyName ?? ''}
              errors={errors}
              required={effectiveRequired}
            />
          </>
        )}
      </div>
    )
  })
HybridLayoutRadioField.displayName = 'HybridLayoutRadioField'

// EMAIL
export const HybridLayoutEmailField: React.FC<HybridLayoutDefaultFieldPropsType> =
  React.memo(({ element, isDisabled, fullLayout, mode }) => {
    const {
      register,
      formState: { errors },
    } = useFormContext()
    return (
      <div>
        {mode === 'view' ? (
          <HybridFieldViewRenderer element={element} fullLayout={fullLayout} />
        ) : (
          <FormInput
            label={element?.meta?.label}
            register={register}
            validation={{
              ...getValidationsFromElement(element),
              pattern: {
                value: EMAIL_REGEX,
                message: NAMESPACES.EMAIL_VALIDATION_MSG,
              },
            }}
            name={element?.keyName || ''}
            disabled={
              !isEmpty(element?.parent)
                ? true
                : typeof isDisabled !== 'undefined'
                ? isDisabled
                : !!element?.isDisabled
            }
            errors={errors}
            required={element?.meta?.required ?? false}
          />
        )}
      </div>
    )
  })
HybridLayoutEmailField.displayName = 'HybridLayoutEmailField'

//MULTI LINE
export const HybridLayoutMultiLineField: React.FC<HybridLayoutDefaultFieldPropsType> =
  React.memo(({ element, isDisabled, fullLayout, mode }) => {
    const {
      register,
      formState: { errors },
    } = useFormContext()
    return (
      <div>
        {mode === 'view' ? (
          <HybridFieldViewRenderer element={element} fullLayout={fullLayout} />
        ) : (
          <MultiLineInput
            label={element?.meta?.label}
            register={register}
            name={element?.keyName || ''}
            disabled={
              !isEmpty(element?.parent)
                ? true
                : typeof isDisabled !== 'undefined'
                ? isDisabled
                : !!element?.isDisabled
            }
            errors={errors}
            required={element?.meta?.required ?? false}
          />
        )}
      </div>
    )
  })
HybridLayoutMultiLineField.displayName = 'HybridLayoutMultiLineField'

//PHONE NUMBER
export const HybridLayoutPhoneNumberField: React.FC<HybridLayoutDefaultFieldPropsType> =
  React.memo(({ element, isDisabled, fullLayout, mode }) => {
    const {
      register,
      formState: { errors },
      setValue,
      getValues,
    } = useFormContext()

    const getPhoneKeys = getValues('__FORM_ORIGINAL_VALUES_PHONE_FIELDS__')
    if (!getPhoneKeys?.includes(element?.meta?.originalKeyName)) {
      setValue('__FORM_ORIGINAL_VALUES_PHONE_FIELDS__', [
        ...(getPhoneKeys || []),
        element?.meta?.originalKeyName,
      ])
    }

    return (
      <div>
        {mode === 'view' ? (
          <HybridFieldViewRenderer element={element} fullLayout={fullLayout} />
        ) : (
          <PhoneInput
            label={element?.meta?.label}
            register={register}
            validation={{
              ...getValidationsFromElement(element),
              pattern: {
                value: PHONE_REGEX,
                message: NAMESPACES.PHONE_VALIDATION_MSG,
              },
            }}
            disabled={
              !isEmpty(element?.parent)
                ? true
                : typeof isDisabled !== 'undefined'
                ? isDisabled
                : !!element?.isDisabled
            }
            setValue={setValue}
            name={element?.keyName || ''}
            errors={errors}
            required={element?.meta?.required ?? false}
          />
        )}
      </div>
    )
  })
HybridLayoutPhoneNumberField.displayName = 'HybridLayoutPhoneNumberField'

//DROPDOWN
export const HybridLayoutDropdownField: React.FC<HybridLayoutDefaultFieldPropsType> =
  React.memo(({ element, isDisabled, fullLayout, mode }) => {
    const {
      control,
      formState: { errors },
      setValue,
    } = useFormContext()

    return (
      <div>
        {mode === 'view' ? (
          <HybridFieldViewRenderer element={element} fullLayout={fullLayout} />
        ) : (
          <ControlledDropDown
            tabIndex={element.tabIndex}
            name={element.keyName}
            errors={errors}
            label={element.meta.label}
            control={control}
            isSearchable={true}
            onChange={(e: DropdownType<string>) => {
              setValue(element.keyName, e?.value)
            }}
            options={(
              element?.meta?.control_values as unknown as string[]
            )?.map((item: string) => ({
              label: item ?? '',
              value: item ?? '',
            }))}
            required={element.meta.required ?? false}
            disabled={isDisabled}
            dataQaId={element.keyName ?? ''}
            customValidation={(value, options, required) => {
              if (required) {
                const defaultOptionsExists =
                  element?.keyName !== NAMESPACES.RECORD_STATUS_LOWERCASE ||
                  options.some((opt) => opt.value === value)
                return defaultOptionsExists || NAMESPACES.REQUIRED_FIELD
              }
              return true
            }}
          />
        )}
      </div>
    )
  })
HybridLayoutDropdownField.displayName = 'HybridLayoutDropdownField'

const getMultiSelectOptions = (element: ElementType) => {
  const controlValues = element?.meta?.control_values
  if (Array.isArray(controlValues) && controlValues.length > 0) {
    return controlValues
      .filter((item) => item)
      .map((item) => ({ label: item, value: item }))
  }
  return (element?.meta?.options || []).map((option) =>
    typeof option === 'string'
      ? { label: option, value: option }
      : { label: option?.label ?? '', value: option?.value ?? '' }
  )
}

export const HybridLayoutMultiSelectDropdownField: React.FC<HybridLayoutDefaultFieldPropsType> =
  React.memo(({ element, isDisabled, mode, fullLayout }) => {
    const {
      control,
      formState: { errors },
      setValue,
    } = useFormContext()
    const selectedValues = useWatch({ name: element.keyName })
    const baseDisabled = !isEmpty(element?.parent)
      ? true
      : typeof isDisabled !== 'undefined'
      ? isDisabled
      : !!element?.isDisabled
    const {
      effectiveDisabled,
      effectiveRequired,
      effectiveHidden,
      triggerFieldChange,
      // hasFormRules,
    } = useFormBehaviorFieldOverrides(
      fullLayout,
      element,
      baseDisabled,
      element?.meta?.required ?? false
    )

    const debouncedTrigger = useMemo(
      () => debounce(() => triggerFieldChange(), 500),
      [triggerFieldChange]
    )

    if (effectiveHidden) {
      return null
    }

    return (
      <div>
        {mode === 'view' ? (
          <div className="font-primary font-light">
            <label className="text-sm font-normal mb-1 block">
              {element?.meta?.label}
            </label>
            <div className="text-xs font-extraLight">
              {Array.isArray(selectedValues) && selectedValues.length > 0
                ? selectedValues.join(', ')
                : '-'}
            </div>
          </div>
        ) : (
          <ControlledMultiSelect
            tabIndex={element.tabIndex}
            name={element.keyName}
            errors={errors}
            label={element.meta.label}
            control={control}
            isSearchable={true}
            onChange={(e: DropdownType<string>[]) => {
              setValue(
                element.keyName,
                (e || []).map((item) => item.value)
              )
              debouncedTrigger()
            }}
            options={getMultiSelectOptions(element)}
            required={effectiveRequired}
            disabled={effectiveDisabled}
            dataQaId={element.keyName ?? ''}
          />
        )}
      </div>
    )
  })
HybridLayoutMultiSelectDropdownField.displayName =
  'HybridLayoutMultiSelectDropdownField'

// NUMBER
export const HybridLayoutNumberField: React.FC<HybridLayoutDefaultFieldPropsType> =
  React.memo(({ element, isDisabled, mode = 'edit', fullLayout }) => {
    const {
      register,
      formState: { errors },
    } = useFormContext()
    const baseDisabled = !isEmpty(element?.parent)
      ? true
      : typeof isDisabled !== 'undefined'
      ? isDisabled
      : !!element?.isDisabled
    const { effectiveDisabled, effectiveRequired, triggerFieldChange } =
      useFormBehaviorFieldOverrides(
        fullLayout,
        element,
        baseDisabled,
        element?.meta?.required ?? false
      )
    const debouncedTrigger = debounce(() => {
      triggerFieldChange()
    }, 500)
    const registerWithFormBehavior: typeof register = (name, options) => {
      const registration = register(name, options)
      if (name !== element.keyName) {
        return registration
      }
      return {
        ...registration,
        onChange: (event) => {
          const result = registration.onChange(event)
          debouncedTrigger()
          return result
        },
      }
    }
    const isFloatControl =
      element?.meta?.control_type === premitiveDataTypes?.float?.value
    return (
      <div>
        {mode === 'view' ? (
          <HybridFieldViewRenderer element={element} fullLayout={fullLayout} />
        ) : (
          <FormInput
            label={element?.meta?.label}
            register={registerWithFormBehavior}
            name={element?.keyName || ''}
            disabled={effectiveDisabled}
            errors={errors}
            required={effectiveRequired}
            type="number"
            controlType={element?.meta?.control_type}
            dataQaId={element?.keyName ?? ''}
            validation={{
              ...getValidationsFromElement(element),
              pattern: isFloatControl
                ? {
                    value: DECIMAL_REGEX,
                    message: NAMESPACES?.INCORRECT_DEFAULT_VALUE,
                  }
                : {
                    value: NUMERIC_REGEX,
                    message: NAMESPACES?.INCORRECT_DEFAULT_VALUE,
                  },
            }}
            onKeyDown={(evt: React.KeyboardEvent<HTMLInputElement>) => {
              const blockedKeys = isFloatControl
                ? ['e', 'E', '+', '-']
                : ['e', 'E', '+', '-', '.']
              if (blockedKeys.includes(evt.key)) {
                evt.preventDefault()
              }
            }}
          />
        )}
      </div>
    )
  })
HybridLayoutNumberField.displayName = 'HybridLayoutNumberField'

// DEFAULT FIELD
export const HybridLayoutDefaultField: React.FC<HybridLayoutDefaultFieldPropsType> =
  React.memo(({ element, isDisabled, mode = 'edit', fullLayout }) => {
    const {
      register,
      formState: { errors },
      setValue,
    } = useFormContext()
    const baseDisabled = !isEmpty(element?.parent)
      ? true
      : typeof isDisabled !== 'undefined'
      ? isDisabled
      : !!element?.isDisabled
    const {
      effectiveDisabled,
      effectiveRequired,
      effectiveHidden,
      triggerFieldChange,
    } = useFormBehaviorFieldOverrides(
      fullLayout,
      element,
      baseDisabled,
      element?.meta?.required ?? false
    )

    if (effectiveHidden) {
      return null
    }

    const debouncedUpdate = debounce(() => {
      triggerFieldChange()
    }, 500)

    return (
      <div>
        {mode === 'view' ? (
          <HybridFieldViewRenderer element={element} fullLayout={fullLayout} />
        ) : (
          <FormInput
            label={element?.meta?.label}
            register={register}
            name={element?.keyName || ''}
            disabled={effectiveDisabled}
            errors={errors}
            required={effectiveRequired}
            onChange={(e) => {
              const newVal = e.target.value.trim()
              setValue(element.keyName, newVal)
              debouncedUpdate()
            }}
          />
        )}
      </div>
    )
  })
HybridLayoutDefaultField.displayName = 'HybridLayoutDefaultField'
